import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Grid, IconButton } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import type { Ciudad } from '../../stores/ciudades/ciudad';
import type { Estado } from '../../stores/estados/estado';
import type { PersonaVende } from '../../stores/personal-vende/persona-vende';
import type {
  ActualizarPersonaVende,
  RegistrarPersonaVende
} from '../../stores/personal-vende/personal-vende.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import transformToOptions from '../../utils/transform-to-options';
import CiudadAddDialog from '../ciudades/CiudadAddDialog';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import EditForm from '../ui/EditForm';

const validationSchema = yup.object({
  id: yup.string(),
  nombre: yup.string().required('El nombre es requerido'),
  idCiudad: yup.number().moreThan(0, 'La ciudad es requerida'),
  idEstado: yup.number().moreThan(0, 'El estado es requerido'),
  direccion: yup.string(),
  telefono1: yup.string(),
  telefono2: yup.string(),
  notas: yup.string()
});

type PersonaVendeFormProps = {
  persona?: PersonaVende;
  ciudades: Ciudad[];
  estados: Estado[];
  onSave:
    | ((values: ActualizarPersonaVende) => Promise<boolean>)
    | ((values: RegistrarPersonaVende) => Promise<number | null>);
  onClose?: () => void;
  reloadCiudades: () => Promise<void>;
} & CommonEntityFormProps;

const PersonaVendeForm = ({
  persona,
  esAgregar,
  ciudades,
  estados,
  onSave,
  onDelete,
  onActivate,
  onClose,
  reloadCiudades
}: PersonaVendeFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || persona?.activa === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore();
  const [showCiudadAddDialog, setShowCiudadAddDialog] = useState(false);
  const initialValues = {
    id: persona?.id ?? '',
    nombre: persona?.nombre ?? '',
    idCiudad: persona?.idCiudad ?? 0,
    idEstado: persona?.idEstado ?? 0,
    direccion: persona?.direccion ?? '',
    telefono1: persona?.telefono1 ?? '',
    telefono2: persona?.telefono2 ?? '',
    notas: persona?.notas ?? ''
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const success = await onSave(values);
      if (success) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  const ciudadesOptions = transformToOptions(ciudades);
  const estadosOptions = transformToOptions(estados);

  const reset = () => formik.resetForm({ values: initialValues });

  const handleCiudadAdd = async (newId: number) => {
    setShowCiudadAddDialog(false);
    await reloadCiudades();
    await formik.setFieldValue('idCiudad', newId);
  };

  let props = {
    module: 'personal-vende',
    title: 'Personal Que Vende Ganado',
    readonly: !activo,
    loading: loading,
    showActivate: false,
    showDelete: esEditar,
    onSave: formik.handleSubmit,
    onClear: reset,
    onDelete: onDelete,
    onActivate: onActivate
  };

  return (
    <>
      <EditForm
        {...(onClose ? { ...props, onClose } : { ...props, returnLink: '/personal-vende' })}
      >
        <Grid>
          <Grid container spacing={2}>
            <Grid>
              <AppTextField
                name="id"
                label="Código"
                placeholder="Código"
                value={formik.values.id}
                disabled={true}
              />
            </Grid>
            <Grid size="grow">
              <AppTextField
                name="nombre"
                label="Nombre"
                placeholder="Nombre"
                maxLength={40}
                disabled={loading || inactivo}
                value={formik.values.nombre}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                helperText={getLengthOrErrorText(
                  formik.touched.nombre ? formik.errors.nombre : '',
                  formik.values.nombre.length,
                  40
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size="grow">
              <AppTextField
                name="direccion"
                label="Dirección"
                placeholder="Dirección"
                maxLength={100}
                disabled={loading || inactivo}
                value={formik.values.direccion}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.direccion && Boolean(formik.errors.direccion)}
                helperText={getLengthOrErrorText(
                  formik.touched.direccion ? formik.errors.direccion : '',
                  formik.values.direccion.length,
                  100
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size="grow">
              <AppTextField
                name="telefono1"
                label="Teléfono 1"
                placeholder="Teléfono 1"
                maxLength={20}
                disabled={loading || inactivo}
                value={formik.values.telefono1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.telefono1 && Boolean(formik.errors.telefono1)}
                helperText={getLengthOrErrorText(
                  formik.touched.telefono1 ? formik.errors.telefono1 : '',
                  formik.values.telefono1.length,
                  20
                )}
              />
            </Grid>
            <Grid size="grow">
              <AppTextField
                name="telefono2"
                label="Teléfono 2"
                placeholder="Teléfono 2"
                maxLength={20}
                disabled={loading || inactivo}
                value={formik.values.telefono2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.telefono2 && Boolean(formik.errors.telefono2)}
                helperText={getLengthOrErrorText(
                  formik.touched.telefono2 ? formik.errors.telefono2 : '',
                  formik.values.telefono2.length,
                  20
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size="grow">
              <ComboBox
                name="idCiudad"
                label="Ciudad"
                disabled={loading || inactivo}
                value={formik.values.idCiudad}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                options={ciudadesOptions}
                error={formik.touched.idCiudad && Boolean(formik.errors.idCiudad)}
                helperText={formik.touched.idCiudad ? formik.errors.idCiudad : ''}
              />
            </Grid>
            <Grid size={1}>
              <IconButton
                disabled={loading || inactivo}
                size="large"
                onClick={() => setShowCiudadAddDialog(true)}
              >
                <AddCircleIcon fontSize="inherit" />
              </IconButton>
            </Grid>
            <Grid size="grow">
              <ComboBox
                name="idEstado"
                label="Estado"
                disabled={loading || inactivo}
                value={formik.values.idEstado}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                options={estadosOptions}
                error={formik.touched.idEstado && Boolean(formik.errors.idEstado)}
                helperText={formik.touched.idEstado ? formik.errors.idEstado : ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container>
            <Grid size="grow">
              <AppTextField
                name="notas"
                label="Notas"
                placeholder="Notas"
                maxLength={200}
                multiline={true}
                rows={2}
                disabled={loading || inactivo}
                value={formik.values.notas}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notas && Boolean(formik.errors.notas)}
                helperText={getLengthOrErrorText(
                  formik.touched.notas ? formik.errors.notas : '',
                  formik.values.notas.length,
                  200
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container>
            <AppTextField label="Status" value={status} disabled={true} />
          </Grid>
        </Grid>
      </EditForm>
      <CiudadAddDialog
        open={showCiudadAddDialog}
        onAddSuccess={handleCiudadAdd}
        onClose={() => setShowCiudadAddDialog(false)}
      />
    </>
  );
};

export default PersonaVendeForm;
